import format from 'date-fns/format'
import React from 'react'

import ChangeDayScheduleEvent from '@flecto-io/analytics/actions/saas/business-hours/change-day-schedule'
import CheckCustomScheduleDayEvent from '@flecto-io/analytics/actions/saas/business-hours/check-custom-schedule-day'
import { formatDateString } from '@flecto/utils'
import { getDayIndex } from '@flecto/utils/src/Date/localizeDayName'
import { type DayOfWeek } from '@flecto/utils/src/Date/ScheduleHours'

import { type BusinessHourContextProps } from '../../../types/BusinessHoursContextProps'
import { type BusinessHourProviderProps } from '../../../types/BusinessHoursProviderProps'
import { useFormContext } from '../AddressForm'
import {
  HourValidation,
  RangeEmitterData,
  RangeHour,
  getDefaultTime,
} from './HoursValidation'

const BusinessHourContext = React.createContext<
  BusinessHourContextProps | undefined
>(undefined)

const BusinessHourProvider: React.FC<
  React.PropsWithChildren<BusinessHourProviderProps>
> = ({ children, dayOfWeek, onChange }) => {
  const [hours, setHours] = React.useState<RangeHour[]>([getDefaultTime()])
  const [checked, setChecked] = React.useState<boolean>(false)
  const form = useFormContext()

  const { invalidHoursKeys, overlappingHoursKeys, duplicateHoursKeys } =
    React.useMemo(() => {
      const validation = new HourValidation(hours)
      return validation.validate()
    }, [hours])

  const handleAddHour = () => {
    setHours([...hours, getDefaultTime()])
    new ChangeDayScheduleEvent(
      dayOfWeek,
      'add',
      String(form.values.address_label)
    ).sendEvent()
  }

  const removeFromKey = (key: string) => {
    const valueToBeRemoved = hours.at(Number(key))
    const newHours = hours.filter((hourInstance) => hourInstance.key !== key)
    setHours(newHours)
    new ChangeDayScheduleEvent(
      dayOfWeek,
      'remove',
      String(form.values.address_label)
    ).sendEvent(
      valueToBeRemoved?.start?.toLocaleTimeString(),
      valueToBeRemoved?.end?.toLocaleTimeString()
    )
  }

  const changeHour = (index: number, date: Date, isStart: boolean) => {
    const newHours = [...hours]

    if (isStart) {
      newHours[index].start = date
    } else {
      newHours[index].end = date
    }
    setHours(newHours)

    const start = formatDateString({
      date: newHours[0].start.toString(),
      variant: 'time-brief',
    })

    const end = formatDateString({
      date: newHours[0].end.toString(),
      variant: 'time-brief',
    })

    new ChangeDayScheduleEvent(
      dayOfWeek,
      'edit',
      String(form.values.address_label)
    ).sendEvent(`2000-01-01T${start}:00`, `2000-01-01T${end}:00`)
  }

  const isInvalidByKey = (key: string) => {
    const isInvalid = invalidHoursKeys.some((timeKey) => timeKey === key)
    const isDuplicate = overlappingHoursKeys.some((timeKey) => timeKey === key)
    return isInvalid || isDuplicate
  }

  const isDuplicateHourByKey = (key: string) => {
    const isDuplicate = duplicateHoursKeys.some((timeKey) => timeKey === key)
    return isDuplicate
  }

  const onChangeValues = React.useMemo(() => {
    if (!checked) {
      return new RangeEmitterData([], dayOfWeek as keyof typeof DayOfWeek)
    }

    return new RangeEmitterData(hours, dayOfWeek as keyof typeof DayOfWeek)
  }, [hours, checked])

  const onFirstMountComponent = () => {
    try {
      const currentData = form.getInputProps('operating_hours_schedule').value

      const i = getDayIndex(dayOfWeek)
      const weekOfDayValues = (currentData[i] as [string, string][]) || []

      const newHours: RangeHour[] = []
      const isClosed = !Object.values(weekOfDayValues).length

      if (!isClosed) {
        setChecked(true)
        Object.values(weekOfDayValues).forEach((value) => {
          const [start, end] = value
          newHours.push(new RangeHour(new Date(start), new Date(end)))
        })
      }

      // Add time field, but it will not consider on update
      if (isClosed) {
        newHours.push(getDefaultTime())
      }
      setHours(newHours)
    } catch (e) {
      console.warn(e)
    }
  }

  const getRangeLabel = (): string => {
    try {
      // get the lower start date and the higher end date
      const startInstance = hours.reduce((prev, current) =>
        prev.start < current.start ? prev : current
      )

      const endInstance = hours.reduce((prev, current) =>
        prev.end > current.end ? prev : current
      )
      const formatLambda = (date: Date) => format(date, 'HH:mm')

      return `${formatLambda(startInstance.start)} - ${formatLambda(
        endInstance.end
      )}`
    } catch {
      return ''
    }
  }

  React.useEffect(() => {
    onFirstMountComponent()
  }, [])

  React.useEffect(() => {
    onChange?.(onChangeValues)
  }, [onChangeValues, checked])

  React.useEffect(() => {
    new CheckCustomScheduleDayEvent(
      dayOfWeek,
      checked,
      String(form.values.address_label)
    ).sendEvent()
  }, [checked])

  return (
    <BusinessHourContext.Provider
      value={{
        handleAddHour,
        removeFromKey,
        changeHour,
        hours,
        invalidHours: {
          invalidHoursKeys,
          overlappingHoursKeys,
          duplicateHoursKeys,
        },
        isInvalidByKey,
        isDuplicateHourByKey,
        checked,
        setChecked: (value) => setChecked(value),
        getRangeLabel,
      }}
    >
      {children}
    </BusinessHourContext.Provider>
  )
}

const useBusinessHour = (): BusinessHourContextProps => {
  const context = React.useContext(BusinessHourContext)

  if (context === undefined) {
    throw new Error(
      'useBusinessHour must be used within a BusinessHourProvider'
    )
  }

  return context
}

export { BusinessHourProvider, useBusinessHour }
